"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModelFieldNumberSagUtil = exports.CollectionModelFieldNumber = void 0;
const core_api_1 = require("@ikegps/core-api");
const catenary_1 = require("../../../math/catenary");
const unit_1 = require("../../../unit");
const collection_field_1 = require("../collection.field");
const number_field_mutator_1 = require("./number.field.mutator");
class CollectionModelFieldNumber extends collection_field_1.CollectionModelField {
    constructor() {
        super(...arguments);
        this._Mutator = new number_field_mutator_1.ModelMutatorNumber(this);
    }
    /** Type guard verify a field is of type number */
    static isNumberField(f) {
        return f.schema.type === CollectionModelFieldNumber.TYPE;
    }
    get Mutator() {
        if (this._Mutator == null) {
            this._Mutator = new number_field_mutator_1.ModelMutatorNumber(this);
        }
        return this._Mutator;
    }
    /**
     * Calculate the value of the number field based off the provider type
     * @returns Raw number value.
     */
    get value() {
        if (this.stateValue == null) {
            return null;
        }
        if (this.isManual()) {
            return this.stateValue.value;
        }
        if (this.isBackBearing()) {
            const capture = this.collection.state.getCapture(this.stateValue.captureId);
            return unit_1.IkeAngle.normalize(capture.orientation.bearing + Math.PI);
        }
        if (this.isBearing()) {
            const capture = this.collection.state.getCapture(this.stateValue.captureId);
            return unit_1.IkeAngle.normalize(capture.orientation.bearing);
        }
        if (this.isSag()) {
            const sagResult = CollectionModelFieldNumberSagUtil.getSag(this);
            if (sagResult == null || isNaN(sagResult.maxSag)) {
                return 0;
            }
            return sagResult.maxSag;
        }
        return null;
    }
    get displayValue() {
        if (this.isManual()) {
            if (isNaN(this.value) || typeof this.value !== 'number') {
                return '';
            }
            return String(unit_1.IkeUnit.round(this.value, unit_1.IkeUnit.DECIMALS));
        }
        if (this.isBackBearing() || this.isBearing()) {
            const degrees = unit_1.IkeUnit.convertAngle(this.value, unit_1.IkeUnit.DEGREE, unit_1.IkeUnit.RADIAN);
            return String(unit_1.IkeUnit.round(degrees, unit_1.IkeUnit.DECIMALS));
        }
        if (this.isSag()) {
            const sagResult = CollectionModelFieldNumberSagUtil.getSag(this);
            if (sagResult == null) {
                return '0';
            }
            return String(unit_1.IkeUnit.round(sagResult.maxSag, unit_1.IkeUnit.DECIMALS));
        }
        return '';
    }
    /**
     * Get the provider type of the field.
     * Defaults to 'manual' if no provider is defined.
     *
     * @returns Field provider type.
     */
    get provider() {
        const stateValue = this.stateValue;
        if (stateValue == null || stateValue.provider == null) {
            return CollectionModelFieldNumber.PROVIDER_MANUAL;
        }
        return stateValue.provider;
    }
    isManual() {
        return this.provider === CollectionModelFieldNumber.PROVIDER_MANUAL;
    }
    isBearing() {
        return this.provider === CollectionModelFieldNumber.PROVIDER_BEARING;
    }
    isBackBearing() {
        return this.provider === CollectionModelFieldNumber.PROVIDER_BACK_BEARING;
    }
    isSag() {
        return this.provider === CollectionModelFieldNumber.PROVIDER_SAG;
    }
    getOwnedCaptures() {
        return this.getCaptures();
    }
    /**
     * All captures used by this field.
     * @returns List of captures
     */
    getCaptures() {
        return this.getCaptureIds()
            .map((c) => this.collection.state.getCapture(c))
            .filter((c) => !!c);
    }
    getOwnedCaptureIds() {
        return this.getCaptureIds();
    }
    getCaptureIds() {
        if (this.isManual()) {
            return [];
        }
        if (this.stateValue == null) {
            return [];
        }
        if (this.isSag()) {
            return [this.stateValue.start, ...this.stateValue.points, this.stateValue.end];
        }
        if (this.isBackBearing() || this.isBearing()) {
            return [this.stateValue.captureId];
        }
        return [];
    }
    /**
     * Whether this field's value is valid
     * @returns Whether the value is valid
     */
    hasValue() {
        const value = this.value;
        if (value == null || isNaN(value)) {
            return false;
        }
        return true;
    }
}
exports.CollectionModelFieldNumber = CollectionModelFieldNumber;
CollectionModelFieldNumber.PROVIDER_MANUAL = 'manual';
CollectionModelFieldNumber.PROVIDER_BEARING = 'bearing';
CollectionModelFieldNumber.PROVIDER_BACK_BEARING = 'backbearing';
CollectionModelFieldNumber.PROVIDER_SAG = 'sag';
CollectionModelFieldNumber.TYPE = core_api_1.IkeFieldType.NUMBER;
class CollectionModelFieldNumberSagUtil {
    static getSag(field) {
        const captures = CollectionModelFieldNumberSagUtil.getSagCaptures(field);
        if (captures == null) {
            return null;
        }
        if (!captures.points[0]) {
            return null;
        }
        const sagOptimizer = catenary_1.CatSagOptimizer.fromCaptures(captures.start, captures.end, captures.points[0]);
        return sagOptimizer.getSag();
    }
    static getSagCaptures(field) {
        if (!field.isSag()) {
            return null;
        }
        const stateValue = field.stateValue;
        const start = field.collection.state.getCapture(stateValue.start);
        const end = field.collection.state.getCapture(stateValue.end);
        const points = stateValue.points
            .map((captureId) => field.collection.state.getCapture(captureId))
            .filter((c) => !!c);
        return { start, end, points };
    }
}
exports.CollectionModelFieldNumberSagUtil = CollectionModelFieldNumberSagUtil;
//# sourceMappingURL=number.field.js.map